<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import googleCalendarAPI from '../../../../api/googleCalendar';
import accountAPI from '../../../../api/account';
import { format } from 'date-fns';
import { getDateFnsLocale } from 'dashboard/composables/useDateFnsLocale';
import { useAlert } from 'dashboard/composables';

export default {
  components: { NextButton },
  data() {
    return {
      isConnected: false,
      isConnecting: false,
      connectionInfo: {},
      calendars: [],
      selectedCalendar: null,
      isSyncing: false,
      isSavingMeetingProvider: false,
      defaultMeetingProvider: 'chatsuite',
      savedMeetingProvider: 'chatsuite',
      meetingRoomTitle: '',
      savedMeetingRoomTitle: '',
      isSavingRoomTitle: false,
      reminderOffsets: [1440, 15],
      // Presets de cadência (minutos ANTES). O cliente liga/desliga.
      reminderPresets: [
        { minutes: 1440, key: 'DAY_BEFORE' },
        { minutes: 180, key: 'H3' },
        { minutes: 60, key: 'H1' },
        { minutes: 30, key: 'M30' },
        { minutes: 15, key: 'M15' },
        { minutes: 5, key: 'M5' },
      ],
      // Lembrete personalizado (padrão de mercado: número + unidade livres)
      customAmount: null,
      customUnit: 'minutes',
      // Config por degrau: minutes -> { message, include_link }
      reminderSteps: {},
    };
  },
  async mounted() {
    this.loadStatus();
    const { data: account } = await accountAPI.get();
    const settings = account.settings || {};
    this.defaultMeetingProvider =
      settings.default_meeting_provider || 'chatsuite';
    this.savedMeetingProvider = this.defaultMeetingProvider;
    this.meetingRoomTitle = settings.meeting_room_title || '';
    this.savedMeetingRoomTitle = this.meetingRoomTitle;
    const offsets = settings.default_reminder_offsets;
    if (Array.isArray(offsets)) {
      this.reminderOffsets = offsets.map(Number).filter(n => !Number.isNaN(n));
    }
    const steps = settings.reminder_steps;
    if (Array.isArray(steps)) {
      steps.forEach(st => {
        this.reminderSteps[Number(st.minutes)] = {
          message: st.message || '',
          include_link: st.include_link !== false,
        };
      });
    }
    window.addEventListener('message', this.handleOAuthCallback);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handleOAuthCallback);
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      const locale = getDateFnsLocale(this.$i18n.locale);
      return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale });
    },

    async connectGoogleCalendar() {
      try {
        this.isConnecting = true;
        const { data } = await googleCalendarAPI.getAuthorizeUrl();

        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        window.open(
          data.authorization_url,
          'Google Calendar OAuth',
          `width=${width},height=${height},left=${left},top=${top}`
        );
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.CONNECT_FAILED'));
      } finally {
        this.isConnecting = false;
      }
    },

    async handleOAuthCallback(event) {
      if (event.data.type === 'google-calendar-connected') {
        await this.loadStatus();
        await this.loadCalendars();
        useAlert(this.$t('CALENDAR.SYNC_SUCCESS'));
      }
    },

    async disconnect() {
      try {
        await googleCalendarAPI.disconnect();
        this.isConnected = false;
        this.connectionInfo = {};
        this.calendars = [];
        useAlert(this.$t('CALENDAR.DISCONNECT'));
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.DISCONNECT_FAILED'));
      }
    },

    async loadStatus() {
      try {
        const { data } = await googleCalendarAPI.getStatus();
        this.isConnected = data.connected;
        this.connectionInfo = data;
        this.selectedCalendar = data.default_calendar_id;

        if (this.isConnected) {
          await this.loadCalendars();
        }
      } catch {
        // Silently fail
      }
    },

    async loadCalendars() {
      try {
        const { data } = await googleCalendarAPI.listCalendars();
        this.calendars = data.calendars;
      } catch {
        // Silently fail
      }
    },

    async setDefaultCalendar() {
      try {
        await googleCalendarAPI.setDefaultCalendar(this.selectedCalendar);
        useAlert(this.$t('CALENDAR.EVENT_UPDATED'));
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.UPDATE_FAILED'));
      }
    },

    async saveDefaultMeetingProvider() {
      try {
        this.isSavingMeetingProvider = true;
        await this.$store.dispatch('accounts/update', {
          default_meeting_provider: this.defaultMeetingProvider,
        });
        this.savedMeetingProvider = this.defaultMeetingProvider;
        useAlert(this.$t('CALENDAR.MEETING_PROVIDER.SAVED'));
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.UPDATE_FAILED'));
      } finally {
        this.isSavingMeetingProvider = false;
      }
    },

    async saveMeetingRoomTitle() {
      try {
        this.isSavingRoomTitle = true;
        const title = this.meetingRoomTitle.trim();
        await this.$store.dispatch('accounts/update', {
          meeting_room_title: title,
        });
        this.meetingRoomTitle = title;
        this.savedMeetingRoomTitle = title;
        useAlert(this.$t('CALENDAR.ROOM_TITLE.SAVED'));
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.UPDATE_FAILED'));
      } finally {
        this.isSavingRoomTitle = false;
      }
    },

    isOffsetOn(minutes) {
      return this.reminderOffsets.includes(minutes);
    },

    toggleOffset(minutes) {
      const idx = this.reminderOffsets.indexOf(minutes);
      if (idx >= 0) {
        this.reminderOffsets.splice(idx, 1);
      } else {
        this.reminderOffsets.push(minutes);
      }
      this.saveReminderCadence();
    },

    // Lembrete personalizado: converte número+unidade em minutos e ativa.
    addCustomOffset() {
      const amount = Number(this.customAmount);
      if (!amount || amount <= 0) return;
      const perUnit = { minutes: 1, hours: 60, days: 1440 };
      const minutes = Math.round(amount * (perUnit[this.customUnit] || 1));
      if (!this.reminderOffsets.includes(minutes)) {
        this.reminderOffsets.push(minutes);
        this.saveReminderCadence();
      }
      this.customAmount = null;
    },

    // "2 dias antes" / "3 h antes" / "45 min antes" para qualquer offset.
    formatOffset(minutes) {
      if (minutes % 1440 === 0) {
        const days = minutes / 1440;
        return this.$t('CALENDAR.REMINDERS.CUSTOM.DAYS_BEFORE', {
          count: days,
        });
      }
      if (minutes % 60 === 0) {
        return this.$t('CALENDAR.REMINDERS.CUSTOM.HOURS_BEFORE', {
          count: minutes / 60,
        });
      }
      return this.$t('CALENDAR.REMINDERS.CUSTOM.MINUTES_BEFORE', {
        count: minutes,
      });
    },

    sortedOffsets() {
      return [...this.reminderOffsets].sort((a, b) => b - a);
    },

    stepFor(minutes) {
      if (!this.reminderSteps[minutes]) {
        this.reminderSteps[minutes] = { message: '', include_link: true };
      }
      return this.reminderSteps[minutes];
    },

    async saveReminderCadence() {
      try {
        await this.$store.dispatch('accounts/update', {
          default_reminder_offsets: [...this.reminderOffsets].sort(
            (a, b) => b - a
          ),
          reminder_steps: [...this.reminderOffsets]
            .sort((a, b) => b - a)
            .map(minutes => ({
              minutes,
              message: this.stepFor(minutes).message,
              include_link: this.stepFor(minutes).include_link,
            })),
        });
        useAlert(this.$t('CALENDAR.REMINDERS.SAVED'));
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.UPDATE_FAILED'));
      }
    },

    async syncNow() {
      this.isSyncing = true;
      try {
        await googleCalendarAPI.sync();
        useAlert(this.$t('CALENDAR.SYNC_STARTED'));
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.SYNC_FAILED'));
      } finally {
        this.isSyncing = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 gap-6 p-6 overflow-y-auto">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ $t('CALENDAR.GOOGLE_CALENDAR') }}
        </h2>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ $t('CALENDAR.CONNECT_GOOGLE_DESC') }}
        </p>
      </div>
    </div>

    <!-- Not Connected State -->
    <div
      v-if="!isConnected"
      class="border border-n-weak rounded-lg p-6 bg-n-solid-1"
    >
      <div class="flex flex-col items-center text-center gap-4">
        <div
          class="w-16 h-16 bg-n-blue-3 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        </div>

        <div>
          <h3 class="font-semibold text-lg mb-2 text-n-slate-12">
            {{ $t('CALENDAR.CONNECT_GOOGLE_ACCOUNT') }}
          </h3>
          <p class="text-sm text-n-slate-11 max-w-md">
            {{ $t('CALENDAR.CONNECT_GOOGLE_ACCOUNT_DESC') }}
          </p>
        </div>

        <button
          class="flex items-center gap-3 px-6 py-3 bg-n-solid-2 hover:bg-n-alpha-2 border border-n-weak rounded-lg shadow-sm transition-all hover:shadow font-medium text-n-slate-12"
          :disabled="isConnecting"
          @click="connectGoogleCalendar"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span v-if="!isConnecting">{{
            $t('CALENDAR.CONNECT_WITH_GOOGLE')
          }}</span>
          <span v-else>{{ $t('CALENDAR.CONNECTING') }}</span>
        </button>
      </div>
    </div>

    <!-- Connected State -->
    <div v-else class="border border-n-weak rounded-lg p-6 bg-n-solid-1">
      <div class="flex items-start justify-between mb-6">
        <div class="flex items-center gap-4">
          <div
            class="w-14 h-14 bg-n-teal-3 rounded-full flex items-center justify-center"
          >
            <svg
              class="w-7 h-7 text-n-teal-11"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <div>
            <div class="flex items-center gap-2 mb-1">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-n-teal-3 text-n-teal-11"
              >
                {{ $t('CALENDAR.CONNECTED') }}
              </span>
            </div>
            <p class="font-medium text-n-slate-12">
              {{ connectionInfo.email }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ $t('CALENDAR.CONNECTED_AT') }}
              {{ formatDate(connectionInfo.connected_at) }}
            </p>
          </div>
        </div>

        <NextButton
          variant="outline"
          color="ruby"
          icon="i-lucide-unplug"
          :label="$t('CALENDAR.DISCONNECT')"
          @click="disconnect"
        />
      </div>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2 text-n-slate-11">
            {{ $t('CALENDAR.DEFAULT_CALENDAR') }}
          </label>
          <select
            v-model="selectedCalendar"
            class="form-select w-full bg-n-background border-n-weak text-n-slate-12"
            @change="setDefaultCalendar"
          >
            <option value="" disabled>
              {{ $t('CALENDAR.SELECT_CALENDAR') }}
            </option>
            <option v-for="cal in calendars" :key="cal.id" :value="cal.id">
              {{ cal.name }} {{ cal.primary ? $t('CALENDAR.PRIMARY') : '' }}
            </option>
          </select>
          <p class="text-xs text-n-slate-10 mt-1">
            {{ $t('CALENDAR.DEFAULT_CALENDAR_DESC') }}
          </p>
        </div>

        <!-- Sync Info -->
        <div class="bg-n-blue-2 border border-n-blue-5 rounded-lg p-4">
          <div class="flex items-start gap-3">
            <svg
              class="w-5 h-5 text-n-blue-11 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h4 class="font-medium text-sm text-n-blue-12">
                {{ $t('CALENDAR.BIDIRECTIONAL_SYNC') }}
              </h4>
              <p class="text-xs text-n-blue-11 mt-1">
                {{ $t('CALENDAR.BIDIRECTIONAL_SYNC_DESC') }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="flex items-center justify-between pt-4 border-t border-n-weak"
        >
          <div>
            <h4 class="font-medium text-sm text-n-slate-12">
              {{ $t('CALENDAR.SYNC_MANUALLY') }}
            </h4>
            <p class="text-xs text-n-slate-11">
              {{ $t('CALENDAR.SYNC_MANUALLY_DESC') }}
            </p>
          </div>

          <button
            type="button"
            class="flex items-center gap-2 px-4 h-9 text-sm font-medium border rounded-lg shadow-sm text-n-slate-12 border-n-weak bg-n-solid-2 hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isSyncing"
            @click="syncNow"
          >
            <fluent-icon
              icon="arrow-sync"
              size="16"
              :class="{ 'animate-spin': isSyncing }"
            />
            {{ $t('CALENDAR.SYNC_NOW') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reuniões e lembretes (sempre visível, independe do Google) -->
    <div class="border border-n-weak rounded-lg p-6 bg-n-solid-1 space-y-6">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('CALENDAR.REMINDERS.LABEL') }}
        </h3>
      </div>

      <!-- Link padrão do lembrete -->
      <div>
        <label class="block text-sm font-medium mb-2 text-n-slate-11">
          {{ $t('CALENDAR.MEETING_PROVIDER.LABEL') }}
        </label>
        <select
          v-model="defaultMeetingProvider"
          class="form-select w-full bg-n-background border-n-weak text-n-slate-12"
        >
          <option value="chatsuite">
            {{ $t('CALENDAR.MEETING_PROVIDER.CHATSUITE') }}
          </option>
          <option value="google_meet">
            {{ $t('CALENDAR.MEETING_PROVIDER.GOOGLE_MEET') }}
          </option>
          <option value="presential">
            {{ $t('CALENDAR.MEETING_PROVIDER.PRESENTIAL') }}
          </option>
          <option value="none">
            {{ $t('CALENDAR.MEETING_PROVIDER.NONE') }}
          </option>
        </select>
        <p class="text-xs text-n-slate-10 mt-1">
          {{ $t('CALENDAR.MEETING_PROVIDER.DESC') }}
        </p>
        <div class="mt-3 flex justify-end">
          <NextButton
            :label="$t('CALENDAR.SAVE')"
            :is-loading="isSavingMeetingProvider"
            :disabled="defaultMeetingProvider === savedMeetingProvider"
            @click="saveDefaultMeetingProvider"
          />
        </div>
      </div>

      <!-- Título da sala de reunião (o que o convidado vê ao entrar) -->
      <div v-if="defaultMeetingProvider === 'chatsuite'">
        <label class="block text-sm font-medium mb-2 text-n-slate-11">
          {{ $t('CALENDAR.ROOM_TITLE.LABEL') }}
        </label>
        <input
          v-model="meetingRoomTitle"
          type="text"
          maxlength="120"
          class="w-full"
          :placeholder="$t('CALENDAR.ROOM_TITLE.PLACEHOLDER')"
        />
        <p class="text-xs text-n-slate-10 mt-1">
          {{ $t('CALENDAR.ROOM_TITLE.DESC') }}
        </p>
        <div class="mt-3 flex justify-end">
          <NextButton
            :label="$t('CALENDAR.SAVE')"
            :is-loading="isSavingRoomTitle"
            :disabled="meetingRoomTitle.trim() === savedMeetingRoomTitle"
            @click="saveMeetingRoomTitle"
          />
        </div>
      </div>

      <!-- Cadência dos lembretes -->
      <div>
        <label class="block text-sm font-medium mb-2 text-n-slate-11">
          {{ $t('CALENDAR.REMINDERS.LABEL') }}
        </label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="preset in reminderPresets"
            :key="preset.minutes"
            type="button"
            class="px-3 py-1.5 rounded-full text-sm border transition-colors"
            :class="
              isOffsetOn(preset.minutes)
                ? 'bg-n-blue-3 border-n-blue-8 text-n-blue-12 font-medium'
                : 'bg-n-background border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="toggleOffset(preset.minutes)"
          >
            {{ $t(`CALENDAR.REMINDERS.PRESETS.${preset.key}`) }}
          </button>
        </div>
        <!-- Personalizado: número + unidade, como no Google Calendar -->
        <div class="flex items-center gap-2 mt-3">
          <input
            v-model.number="customAmount"
            type="number"
            min="1"
            class="form-input w-24 text-sm"
            :placeholder="$t('CALENDAR.REMINDERS.CUSTOM.AMOUNT_PH')"
            @keydown.enter.prevent="addCustomOffset"
          />
          <select v-model="customUnit" class="form-input w-32 text-sm">
            <option value="minutes">
              {{ $t('CALENDAR.REMINDERS.CUSTOM.MINUTES') }}
            </option>
            <option value="hours">
              {{ $t('CALENDAR.REMINDERS.CUSTOM.HOURS') }}
            </option>
            <option value="days">
              {{ $t('CALENDAR.REMINDERS.CUSTOM.DAYS') }}
            </option>
          </select>
          <button
            type="button"
            class="px-3 py-1.5 rounded-full text-sm border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
            @click="addCustomOffset"
          >
            {{ $t('CALENDAR.REMINDERS.CUSTOM.ADD') }}
          </button>
        </div>
        <!-- Ativos: cada degrau com mensagem própria e link sim/não -->
        <div v-if="reminderOffsets.length" class="flex flex-col gap-2 mt-3">
          <div
            v-for="minutes in sortedOffsets()"
            :key="minutes"
            class="rounded-xl border border-n-weak bg-n-solid-1 p-3"
          >
            <div class="flex items-center justify-between">
              <span
                class="inline-flex items-center gap-1 rounded-full bg-n-blue-3 border border-n-blue-8 px-2.5 py-0.5 text-xs font-medium text-n-blue-12"
              >
                {{ formatOffset(minutes) }}
              </span>
              <button
                type="button"
                class="i-lucide-x size-4 text-n-slate-10 hover:text-n-ruby-11"
                @click="toggleOffset(minutes)"
              />
            </div>
            <textarea
              v-model="stepFor(minutes).message"
              rows="2"
              class="reset-base mt-2 w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 !text-sm text-n-slate-12 outline-none focus:border-n-brand"
              :placeholder="$t('CALENDAR.REMINDERS.STEP_MESSAGE_PH')"
              @change="saveReminderCadence"
            />
            <div
              class="mt-1.5 flex flex-wrap items-center justify-between gap-2"
            >
              <label
                class="flex cursor-pointer items-center gap-2 text-xs text-n-slate-11"
              >
                <input
                  v-model="stepFor(minutes).include_link"
                  type="checkbox"
                  class="size-4 accent-[#12B886]"
                  @change="saveReminderCadence"
                />
                {{ $t('CALENDAR.REMINDERS.STEP_INCLUDE_LINK') }}
              </label>
              <span class="text-[11px] text-n-slate-10">
                {{ $t('CALENDAR.REMINDERS.STEP_PLACEHOLDERS') }}
              </span>
            </div>
          </div>
        </div>
        <p class="text-xs text-n-slate-10 mt-2">
          {{ $t('CALENDAR.REMINDERS.DESC') }}
        </p>
        <p
          v-if="reminderOffsets.length === 0"
          class="text-xs text-n-amber-11 mt-1"
        >
          {{ $t('CALENDAR.REMINDERS.EMPTY') }}
        </p>
      </div>
    </div>
  </div>
</template>
